import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getVideoById, videos, getLatestVideos } from '@/mocks/videos';

export default function VideoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id || '');
  const [expanded, setExpanded] = useState(false);

  if (!video) {
    return (
      <Layout>
        <div className="pt-20 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">视频未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">抱歉，您要查看的视频不存在或已下架。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const relatedVideos = video.relatedVideos
    .map(rid => videos.find(v => v.id === rid))
    .filter(Boolean) as typeof videos;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: video.title,
    description: video.description,
    thumbnailUrl: video.thumbnail,
    uploadDate: video.date,
    duration: video.duration,
    contentUrl: `https://www.jiujiu-video.com/video/${video.id}`,
    embedUrl: `https://www.jiujiu-video.com/embed/${video.id}`,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: video.views,
    },
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: video.date,
    author: {
      '@type': 'Organization',
      name: '玖玖视频',
    },
    publisher: {
      '@type': 'Organization',
      name: '玖玖视频',
    },
  };

  return (
    <Layout>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <div className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: video.category, href: `/category/${video.categorySlug}` },
              { label: video.title },
            ]}
          />

          <div className="py-6 md:py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="relative overflow-hidden rounded-lg aspect-video bg-foreground-950 mb-6">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    title={`${video.title} - 玖玖视频`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center cursor-pointer group">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 group-hover:bg-primary-500 transition-all group-hover:scale-110">
                      <i className="ri-play-fill text-3xl md:text-4xl text-background-50 ml-1"></i>
                    </div>
                  </div>
                </div>

                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line"></i>
                    </div>
                    {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万次观看` : `${video.views}次观看`}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500"></i>
                    </div>
                    {video.rating}分
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line"></i>
                    </div>
                    {video.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </div>
                    {video.duration}
                  </span>
                  <span className="bg-secondary-100 text-secondary-700 text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                    {video.category}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {video.tags.map(tag => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs border border-background-200 cursor-default">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-5 mb-8">
                  <div className={`text-sm text-foreground-700 leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}>
                    {video.content}
                  </div>
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
                  >
                    {expanded ? '收起' : '展开全部'}
                  </button>
                </div>
              </div>

              <div className="lg:col-span-1">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">
                  相关推荐
                </h3>
                <div className="space-y-4" data-product-shop>
                  {relatedVideos.slice(0, 6).map(rel => (
                    <Link
                      key={rel.id}
                      to={`/video/${rel.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="w-40 flex-shrink-0 relative overflow-hidden rounded-md aspect-video bg-background-200">
                        <img
                          src={rel.thumbnail}
                          alt={rel.title}
                          title={`${rel.title} - 玖玖视频`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute bottom-1.5 right-1.5 bg-black/75 text-background-50 text-[10px] px-1.5 py-0.5 rounded font-medium">
                          {rel.duration}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-heading font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                          {rel.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-foreground-500">
                          <span className="flex items-center gap-0.5">
                            <div className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-eye-line"></i>
                            </div>
                            {rel.views >= 10000 ? `${(rel.views / 10000).toFixed(1)}万` : rel.views}
                          </span>
                          <span>{rel.date}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}